import { Environment, Html, OrbitControls, useProgress } from '@react-three/drei';
import { Canvas as CanvasUI, useThree } from '@react-three/fiber';
import { Wrapper } from './Canvas.style';
// import { Bloom, EffectComposer, Vignette } from '@react-three/postprocessing';
import classnames from 'classnames';
import { Suspense, useEffect, useRef } from 'react';
import { useKey } from 'react-use';
import { setCamera } from '../../utils/camera';
import { setScene } from '../../utils/models';
import { useSpin } from './Canvas.hooks';
import { PresetsType } from './Canvas.types';
import { takeScreenshot } from './Canvas.utils';

export type CanvasProps = {
  cameraTarget?: number[];
  cameraPosition?: number[];
  minDistance?: number;
  children?: React.ReactNode;
  environment?: PresetsType;
  ambientLightIntensity?: number;
  noBackground?: boolean;
  screenshotFileName?: string;
};

const DEFAULT_CAMERA_POSITION = [0.5, 1, 0.5];
const DEFAULT_CAMERA_TARGET = [0, 0, 0];
const DEFAULT_MIN_DISTANCE = 0.1;
const DEFAULT_AMBIENT_LIGHT_INTENSITY = 0.5;

function Loader() {
  const { progress } = useProgress();
  return (
    <Html center style={{ whiteSpace: 'nowrap' }}>
      {progress.toFixed(0)}% loading
    </Html>
  );
}

export function ThreeInner(props: CanvasProps) {
  const {
    minDistance = DEFAULT_MIN_DISTANCE,
    cameraTarget = DEFAULT_CAMERA_TARGET,
    cameraPosition = DEFAULT_CAMERA_POSITION,
    ambientLightIntensity = DEFAULT_AMBIENT_LIGHT_INTENSITY,
    environment,
    screenshotFileName,
  } = props;
  const orbitRef = useRef<any>();
  const { scene, camera } = useThree();

  setCamera(camera);
  setScene(scene);
  useSpin(orbitRef);

  useEffect(() => {
    if (orbitRef.current) {
      orbitRef.current.target.set(cameraTarget[0], cameraTarget[1], cameraTarget[2]); // Set the target position
      orbitRef.current.minDistance = minDistance;
      orbitRef.current.object.position.set(cameraPosition[0], cameraPosition[1], cameraPosition[2]); // Set the camera position
      orbitRef.current.object.lookAt(cameraTarget[0], cameraTarget[1], cameraTarget[2]); // Ensure the camera looks at the target
      orbitRef.current.update(); // Ensure the changes take effect
    }
  }, []);

  useKey(
    '`',
    (ev: any) => {
      if (!ev.ctrlKey) return;
      ev.preventDefault();
      ev.stopPropagation();
      takeScreenshot(scene, camera, screenshotFileName);
    },
    {},
    [scene, camera, screenshotFileName]
  );

  return (
    <>
      <ambientLight intensity={0.5} />
      <directionalLight position={[10, 10, 5]} intensity={0.4} />
      <pointLight position={[-10, -10, -10]} intensity={0.3} />
      {environment && <Environment preset={environment} background={false} />}
      <ambientLight intensity={ambientLightIntensity} />
      <directionalLight position={[5, 5, 5]} intensity={0.4} />
      <OrbitControls ref={orbitRef} />
      {props.children}
    </>
  );
}

export function Canvas(props: CanvasProps) {
  const { noBackground } = props;

  const className = classnames('Canvas-wrapper', {
    noBackground,
  });

  return (
    <Wrapper className={className} data-testid='Canvas-wrapper'>
      <CanvasUI>
        <Suspense fallback={<Loader />}>
          <ThreeInner {...props} />
        </Suspense>
      </CanvasUI>
    </Wrapper>
  );
}

export default Canvas;
